import * as THREE from 'three/webgpu';

import { triangulatePolygon, polygonArea } from './polygon.js';
import { boxUV } from './uvtools.js';

/**
 * @file Non-indexed, flat-shaded primitive factories — the atoms every level
 * generator builds from.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * INVARIANTS every factory here guarantees
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. `geo.index === null`. Always. Merged/baked geometry must never be indexed
 *    (types.js rule 3) and it is cheaper to never create an index than to strip
 *    one later.
 * 2. **Origin is at the BASE**: y = 0 is the bottom face / footprint plane, the
 *    shape extends up +Y. Drop a prim at `heightAt(x,z)` and it sits on the
 *    ground with no fudge factor. Centred on X and Z.
 * 3. Exact per-FACE normals, written directly (not averaged) — this IS the
 *    flat-shaded look, and the bake reads these normals for N·L.
 * 4. **UVs are in METRES** (1 UV unit = 1 world unit). Retile with
 *    `uvtools.scaleUV(geo, 1/tileMetres)` or reproject with `boxUV`.
 * 5. Deterministic: the only factory that randomises is {@link icoRock}, and it
 *    hashes a seed — no `Math.random()` anywhere.
 *
 * Winding is CCW-outside throughout, so `material.side = FrontSide` works.
 *
 * @module engine/build/prims
 */

/** @typedef {[number, number]} P2 */
/** @typedef {[number, number, number]} P3 */

// ─────────────────────────────────────────────────────────────────────────────
// Internal accumulator
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tiny push-only triangle sink. Plain arrays are fine here: prims are small
 * (tens of triangles) and get funnelled into `GeoBuilder`'s typed-array chunks
 * immediately. Bulk paths (heightfields, poly prisms in GeoBuilder) write typed
 * arrays directly and never touch this.
 */
class Raw {
  constructor() {
    /** @type {number[]} */ this.pos = [];
    /** @type {number[]} */ this.nor = [];
    /** @type {number[]} */ this.uv = [];
  }

  /**
   * @param {P3} a @param {P3} b @param {P3} c
   * @param {P2} ua @param {P2} ub @param {P2} uc
   * @param {P3|null} [normal] Pass one to force a shared (exactly equal) normal.
   */
  tri(a, b, c, ua, ub, uc, normal) {
    let nx;
    let ny;
    let nz;
    if (normal) {
      nx = normal[0];
      ny = normal[1];
      nz = normal[2];
    } else {
      const ux = b[0] - a[0];
      const uy = b[1] - a[1];
      const uz = b[2] - a[2];
      const vx = c[0] - a[0];
      const vy = c[1] - a[1];
      const vz = c[2] - a[2];
      nx = uy * vz - uz * vy;
      ny = uz * vx - ux * vz;
      nz = ux * vy - uy * vx;
      const l = Math.hypot(nx, ny, nz) || 1;
      nx /= l;
      ny /= l;
      nz /= l;
    }
    this.pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
    this.nor.push(nx, ny, nz, nx, ny, nz, nx, ny, nz);
    this.uv.push(ua[0], ua[1], ub[0], ub[1], uc[0], uc[1]);
  }

  /**
   * Planar quad `a-b-c-d` (CCW seen from outside). ONE normal is computed from
   * the diagonals and shared by both triangles, so a planar quad never shows a
   * hairline shading seam from float error.
   * @param {P3} a @param {P3} b @param {P3} c @param {P3} d
   * @param {P2} ua @param {P2} ub @param {P2} uc @param {P2} ud
   */
  quad(a, b, c, d, ua, ub, uc, ud) {
    const ux = c[0] - a[0];
    const uy = c[1] - a[1];
    const uz = c[2] - a[2];
    const vx = d[0] - b[0];
    const vy = d[1] - b[1];
    const vz = d[2] - b[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l;
    ny /= l;
    nz /= l;
    /** @type {P3} */
    const n = [nx, ny, nz];
    this.tri(a, b, c, ua, ub, uc, n);
    this.tri(a, c, d, ua, uc, ud, n);
  }

  /** @returns {THREE.BufferGeometry} */
  build() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nor, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    return g;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Boxes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Axis-aligned box, base at y = 0, centred on XZ.
 * @param {number} w Extent along X.
 * @param {number} h Height along +Y.
 * @param {number} d Extent along Z.
 * @returns {THREE.BufferGeometry} 12 triangles.
 */
export function box(w, h, d) {
  return taperedBox(w, h, d, { taperX: 1, taperZ: 1 });
}

/**
 * Box whose top face is scaled by `taperX`/`taperZ`. `taper < 1` gives a
 * frustum (silo, tapered tower, tree trunk); `taper > 1` flares outward. Every
 * side stays planar, so the flat shading is clean.
 * @param {number} w
 * @param {number} h
 * @param {number} d
 * @param {{ taperX?: number, taperZ?: number, caps?: 'both'|'top'|'none' }} [opts]
 * @returns {THREE.BufferGeometry}
 */
export function taperedBox(w, h, d, { taperX = 1, taperZ = 1, caps = 'both' } = {}) {
  const hx = w * 0.5;
  const hz = d * 0.5;
  const tx = hx * taperX;
  const tz = hz * taperZ;
  const r = new Raw();

  // +X
  r.quad(
    [hx, 0, hz], [hx, 0, -hz], [tx, h, -tz], [tx, h, tz],
    [0, 0], [d, 0], [d, h], [0, h],
  );
  // -X
  r.quad(
    [-hx, 0, -hz], [-hx, 0, hz], [-tx, h, tz], [-tx, h, -tz],
    [0, 0], [d, 0], [d, h], [0, h],
  );
  // +Z
  r.quad(
    [-hx, 0, hz], [hx, 0, hz], [tx, h, tz], [-tx, h, tz],
    [0, 0], [w, 0], [w, h], [0, h],
  );
  // -Z
  r.quad(
    [hx, 0, -hz], [-hx, 0, -hz], [-tx, h, -tz], [tx, h, -tz],
    [0, 0], [w, 0], [w, h], [0, h],
  );
  if (caps !== 'none') {
    // +Y
    r.quad(
      [-tx, h, tz], [tx, h, tz], [tx, h, -tz], [-tx, h, -tz],
      [0, 0], [tx * 2, 0], [tx * 2, tz * 2], [0, tz * 2],
    );
  }
  if (caps === 'both') {
    // -Y
    r.quad(
      [-hx, 0, -hz], [hx, 0, -hz], [hx, 0, hz], [-hx, 0, hz],
      [0, 0], [w, 0], [w, d], [0, d],
    );
  }
  return r.build();
}

// ─────────────────────────────────────────────────────────────────────────────
// Rotational
// ─────────────────────────────────────────────────────────────────────────────

/**
 * N-sided cylinder / prism, base at y = 0. `sides` of 5–8 is the low-poly
 * sweet spot (poles, barrels, tanks, tree trunks).
 * @param {number} r Radius.
 * @param {number} h Height.
 * @param {number} [sides=6]
 * @param {{ caps?: 'both'|'top'|'none', phase?: number }} [opts] `phase` rotates
 *   the ring (radians) so a row of instanced poles is not visibly identical.
 * @returns {THREE.BufferGeometry}
 */
export function cylN(r, h, sides = 6, { caps = 'both', phase = 0 } = {}) {
  const n = Math.max(3, sides | 0);
  const raw = new Raw();
  const step = (Math.PI * 2) / n;
  const arc = r * step; // side quad width in metres

  /** @type {P2[]} */
  const ring = [];
  for (let i = 0; i < n; i++) {
    const a = phase + i * step;
    ring.push([Math.cos(a) * r, Math.sin(a) * r]);
  }

  for (let i = 0; i < n; i++) {
    const p = ring[i];
    const q = ring[(i + 1) % n];
    const u0 = i * arc;
    const u1 = (i + 1) * arc;
    raw.quad(
      [p[0], 0, p[1]], [p[0], h, p[1]], [q[0], h, q[1]], [q[0], 0, q[1]],
      [u0, 0], [u0, h], [u1, h], [u1, 0],
    );
  }

  if (caps !== 'none') {
    /** @type {P3} */
    const up = [0, 1, 0];
    for (let i = 0; i < n; i++) {
      const p = ring[i];
      const q = ring[(i + 1) % n];
      raw.tri(
        [0, h, 0], [q[0], h, q[1]], [p[0], h, p[1]],
        [0, 0], [q[0], q[1]], [p[0], p[1]],
        up,
      );
    }
  }
  if (caps === 'both') {
    /** @type {P3} */
    const dn = [0, -1, 0];
    for (let i = 0; i < n; i++) {
      const p = ring[i];
      const q = ring[(i + 1) % n];
      raw.tri(
        [0, 0, 0], [p[0], 0, p[1]], [q[0], 0, q[1]],
        [0, 0], [p[0], p[1]], [q[0], q[1]],
        dn,
      );
    }
  }
  return raw.build();
}

/**
 * N-sided cone, base at y = 0, apex at (0, h, 0). Roof cones, tree canopies,
 * traffic cones, spikes.
 * @param {number} r
 * @param {number} h
 * @param {number} [sides=6]
 * @param {{ cap?: boolean, phase?: number }} [opts]
 * @returns {THREE.BufferGeometry}
 */
export function coneN(r, h, sides = 6, { cap = true, phase = 0 } = {}) {
  const n = Math.max(3, sides | 0);
  const raw = new Raw();
  const step = (Math.PI * 2) / n;
  const arc = r * step;
  const slant = Math.hypot(r, h);

  /** @type {P2[]} */
  const ring = [];
  for (let i = 0; i < n; i++) {
    const a = phase + i * step;
    ring.push([Math.cos(a) * r, Math.sin(a) * r]);
  }

  for (let i = 0; i < n; i++) {
    const p = ring[i];
    const q = ring[(i + 1) % n];
    raw.tri(
      [p[0], 0, p[1]], [0, h, 0], [q[0], 0, q[1]],
      [i * arc, 0], [(i + 0.5) * arc, slant], [(i + 1) * arc, 0],
    );
  }
  if (cap) {
    /** @type {P3} */
    const dn = [0, -1, 0];
    for (let i = 0; i < n; i++) {
      const p = ring[i];
      const q = ring[(i + 1) % n];
      raw.tri(
        [0, 0, 0], [p[0], 0, p[1]], [q[0], 0, q[1]],
        [0, 0], [p[0], p[1]], [q[0], q[1]],
        dn,
      );
    }
  }
  return raw.build();
}

// ─────────────────────────────────────────────────────────────────────────────
// Extrusion
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extrudes a footprint polygon into a vertical prism. THE building/lot/roof
 * workhorse.
 *
 * `poly` is `[[x, z], ...]` in WORLD XZ (not local — the prism is built where
 * the polygon is, so no matrix is needed), CCW by the `polygon.js` convention
 * (`polygonArea > 0`); clockwise input is silently reversed.
 *
 * Wall UVs run `u` = distance along the perimeter, `v` = height. Cap UVs are
 * planar XZ. Both in metres.
 *
 * @param {Array<[number, number]>} poly
 * @param {number} h Height above `y0`.
 * @param {{ y0?: number, caps?: 'both'|'top'|'none' }} [opts]
 * @returns {THREE.BufferGeometry}
 */
export function prismFromPolygon(poly, h, { y0 = 0, caps = 'both' } = {}) {
  const src = polygonArea(poly) >= 0 ? poly : poly.slice().reverse();
  const n = src.length;
  const raw = new Raw();
  if (n < 3) return raw.build();

  const y1 = y0 + h;

  // Walls. Outward normal for a positively-wound polygon is (ez, -ex); this
  // vertex order produces exactly that (see polygon.js header).
  let run = 0;
  for (let i = 0; i < n; i++) {
    const p = src[i];
    const q = src[(i + 1) % n];
    const len = Math.hypot(q[0] - p[0], q[1] - p[1]);
    raw.quad(
      [p[0], y0, p[1]], [p[0], y1, p[1]], [q[0], y1, q[1]], [q[0], y0, q[1]],
      [run, y0], [run, y1], [run + len, y1], [run + len, y0],
    );
    run += len;
  }

  if (caps !== 'none') {
    const tris = triangulatePolygon(src);
    /** @type {P3} */
    const up = [0, 1, 0];
    /** @type {P3} */
    const dn = [0, -1, 0];
    for (const [a, b, c] of tris) {
      const pa = src[a];
      const pb = src[b];
      const pc = src[c];
      // Positively-wound XZ triangles point -Y, so the TOP cap is reversed.
      raw.tri(
        [pc[0], y1, pc[1]], [pb[0], y1, pb[1]], [pa[0], y1, pa[1]],
        [pc[0], pc[1]], [pb[0], pb[1]], [pa[0], pa[1]],
        up,
      );
      if (caps === 'both') {
        raw.tri(
          [pa[0], y0, pa[1]], [pb[0], y0, pb[1]], [pc[0], y0, pc[1]],
          [pa[0], pa[1]], [pb[0], pb[1]], [pc[0], pc[1]],
          dn,
        );
      }
    }
  }
  return raw.build();
}

/**
 * Single up-facing quad at y = 0, centred on XZ. Ground slabs, sidewalk plates,
 * decal bases, water surfaces.
 * @param {number} w
 * @param {number} d
 * @returns {THREE.BufferGeometry} 2 triangles.
 */
export function plateY(w, d) {
  const hx = w * 0.5;
  const hz = d * 0.5;
  const raw = new Raw();
  raw.quad(
    [-hx, 0, hz], [hx, 0, hz], [hx, 0, -hz], [-hx, 0, -hz],
    [0, 0], [w, 0], [w, d], [0, d],
  );
  return raw.build();
}

// ─────────────────────────────────────────────────────────────────────────────
// Tube
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Polyline tube with rotation-minimising frames (no twist popping, no gimbal
 * blow-up on vertical runs). Cables, pipes, branches, handrails, rebar.
 *
 * NOTE unlike the other factories the tube is built at the path's own
 * coordinates — the "base at y=0" rule does not apply, because a path already
 * says where it is.
 *
 * @param {Array<[number, number, number]|{x: number, y: number, z: number}>} path
 *   >= 2 points. Consecutive duplicates are dropped.
 * @param {number|number[]} [r=0.1] Radius; an array gives a per-point radius
 *   (taper a branch, swell a pipe joint). Shorter arrays clamp to the last value.
 * @param {number} [sides=6]
 * @param {{ caps?: boolean }} [opts]
 * @returns {THREE.BufferGeometry}
 */
export function tube(path, r = 0.1, sides = 6, { caps = true } = {}) {
  /** @type {P3[]} */
  const pts = [];
  for (const p of path) {
    /** @type {P3} */
    const q = Array.isArray(p) ? [p[0], p[1], p[2]] : [p.x, p.y, p.z];
    const last = pts[pts.length - 1];
    if (last && Math.hypot(q[0] - last[0], q[1] - last[1], q[2] - last[2]) < 1e-7) continue;
    pts.push(q);
  }
  const m = pts.length;
  const raw = new Raw();
  if (m < 2) return raw.build();

  const n = Math.max(3, sides | 0);
  const radAt = (i) => (Array.isArray(r) ? (r[Math.min(i, r.length - 1)] ?? 0.1) : r);

  // Tangents (central differences inside, one-sided at the ends).
  /** @type {P3[]} */
  const tan = [];
  for (let i = 0; i < m; i++) {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(m - 1, i + 1)];
    let tx = b[0] - a[0];
    let ty = b[1] - a[1];
    let tz = b[2] - a[2];
    const l = Math.hypot(tx, ty, tz) || 1;
    tan.push([tx / l, ty / l, tz / l]);
  }

  // Parallel transport: seed one normal, rotate it along the polyline.
  /** @type {P3} */
  let nrm = perp(tan[0]);
  /** @type {P3[]} */
  const N = [nrm];
  for (let i = 1; i < m; i++) {
    nrm = rotateTo(nrm, tan[i - 1], tan[i]);
    // Re-orthogonalise against drift.
    const d = nrm[0] * tan[i][0] + nrm[1] * tan[i][1] + nrm[2] * tan[i][2];
    nrm = norm3([nrm[0] - tan[i][0] * d, nrm[1] - tan[i][1] * d, nrm[2] - tan[i][2] * d]);
    N.push(nrm);
  }

  // Rings.
  /** @type {P3[][]} */
  const rings = [];
  /** @type {number[]} */
  const runs = [0];
  for (let i = 0; i < m; i++) {
    if (i > 0) {
      runs[i] = runs[i - 1] + Math.hypot(
        pts[i][0] - pts[i - 1][0],
        pts[i][1] - pts[i - 1][1],
        pts[i][2] - pts[i - 1][2],
      );
    }
    const t = tan[i];
    const nn = N[i];
    const bb = cross3(t, nn); // (n, b, t) is right-handed => outward normals
    const rad = radAt(i);
    /** @type {P3[]} */
    const ring = [];
    for (let j = 0; j < n; j++) {
      const a = (j / n) * Math.PI * 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      ring.push([
        pts[i][0] + (nn[0] * ca + bb[0] * sa) * rad,
        pts[i][1] + (nn[1] * ca + bb[1] * sa) * rad,
        pts[i][2] + (nn[2] * ca + bb[2] * sa) * rad,
      ]);
    }
    rings.push(ring);
  }

  const circ = (Math.PI * 2 * radAt(0)) / n;
  for (let i = 0; i < m - 1; i++) {
    for (let j = 0; j < n; j++) {
      const j1 = (j + 1) % n;
      raw.quad(
        rings[i][j], rings[i][j1], rings[i + 1][j1], rings[i + 1][j],
        [j * circ, runs[i]], [(j + 1) * circ, runs[i]],
        [(j + 1) * circ, runs[i + 1]], [j * circ, runs[i + 1]],
      );
    }
  }

  if (caps) {
    const t0 = tan[0];
    /** @type {P3} */
    const nStart = [-t0[0], -t0[1], -t0[2]];
    for (let j = 1; j < n - 1; j++) {
      raw.tri(
        rings[0][0], rings[0][j + 1], rings[0][j],
        [0, 0], [j + 1, 0], [j, 0],
        nStart,
      );
    }
    const last = m - 1;
    for (let j = 1; j < n - 1; j++) {
      raw.tri(
        rings[last][0], rings[last][j], rings[last][j + 1],
        [0, 0], [j, 0], [j + 1, 0],
        tan[last],
      );
    }
  }
  return raw.build();
}

// ─────────────────────────────────────────────────────────────────────────────
// Rock
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Seeded, perturbed icosahedron — boulders, rubble, debris chunks, asteroid-ish
 * wasteland junk. Base sits at y = 0 so it can be dropped straight onto
 * `heightAt`.
 *
 * The displacement is hashed from the QUANTISED ORIGINAL POSITION, not from a
 * running rng: on non-indexed geometry the same logical corner appears in 5–6
 * triangles, and any per-vertex randomness would tear it open. Same seed =>
 * byte-identical rock, always.
 *
 * @param {string|number} seed
 * @param {{ radius?: number, detail?: number, jitter?: number, squash?: number }} [opts]
 *   `detail` 0 = 20 faces, 1 = 80, 2 = 320 (1 is the default and the look).
 *   `jitter` is the radial displacement fraction (0.25 = +/-25%).
 *   `squash` (default 0.18) is how much the non-uniform axis scaling may
 *   deviate from 1, which is what stops rocks reading as spheres.
 * @returns {THREE.BufferGeometry}
 */
export function icoRock(seed, { radius = 1, detail = 1, jitter = 0.25, squash = 0.18 } = {}) {
  const s = hashStr(seed);
  let src = new THREE.IcosahedronGeometry(radius, Math.max(0, detail | 0));
  if (src.index !== null) {
    const flat = src.toNonIndexed();
    src.dispose();
    src = flat;
  }

  const pos = /** @type {THREE.BufferAttribute} */ (src.getAttribute('position'));
  const P = /** @type {Float32Array} */ (pos.array);

  // Per-rock axis scaling (deterministic from the seed alone).
  const sx = 1 + (hashToUnit(mix32(s, 0x51ed270b)) * 2 - 1) * squash;
  const sy = 1 + (hashToUnit(mix32(s, 0x1b873593)) * 2 - 1) * squash;
  const sz = 1 + (hashToUnit(mix32(s, 0xcc9e2d51)) * 2 - 1) * squash;

  const q = 1 / Math.max(1e-6, radius * 1e-3); // quantiser for the vertex key
  let minY = Infinity;
  for (let i = 0; i < P.length; i += 3) {
    const kx = Math.round(P[i] * q);
    const ky = Math.round(P[i + 1] * q);
    const kz = Math.round(P[i + 2] * q);
    const h = hashToUnit(hash3i(kx, ky, kz, s));
    const scale = 1 + (h * 2 - 1) * jitter;
    P[i] = P[i] * scale * sx;
    P[i + 1] = P[i + 1] * scale * sy;
    P[i + 2] = P[i + 2] * scale * sz;
    if (P[i + 1] < minY) minY = P[i + 1];
  }
  for (let i = 1; i < P.length; i += 3) P[i] -= minY;

  pos.needsUpdate = true;
  src.computeVertexNormals(); // non-indexed => exact per-face normals
  boxUV(src, 1);
  src.computeBoundingBox();
  src.computeBoundingSphere();
  return src;
}

// ─────────────────────────────────────────────────────────────────────────────
// Small vector + hash helpers (kept local: prims must not depend on THREE.Vector3
// allocation in hot loops)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {P3} v
 * @returns {P3}
 */
function norm3(v) {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

/**
 * @param {P3} a
 * @param {P3} b
 * @returns {P3}
 */
function cross3(a, b) {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

/**
 * Any unit vector perpendicular to `t`.
 * @param {P3} t
 * @returns {P3}
 */
function perp(t) {
  const ax = Math.abs(t[0]);
  const ay = Math.abs(t[1]);
  const az = Math.abs(t[2]);
  /** @type {P3} */
  const other = ax <= ay && ax <= az ? [1, 0, 0] : ay <= az ? [0, 1, 0] : [0, 0, 1];
  return norm3(cross3(t, other));
}

/**
 * Rodrigues rotation of `v` by the rotation that carries `from` onto `to`.
 * @param {P3} v
 * @param {P3} from
 * @param {P3} to
 * @returns {P3}
 */
function rotateTo(v, from, to) {
  const axis = cross3(from, to);
  const s = Math.hypot(axis[0], axis[1], axis[2]);
  const c = from[0] * to[0] + from[1] * to[1] + from[2] * to[2];
  if (s < 1e-9) return c >= 0 ? v : [-v[0], -v[1], -v[2]];
  const k = [axis[0] / s, axis[1] / s, axis[2] / s];
  const ang = Math.atan2(s, c);
  const ca = Math.cos(ang);
  const sa = Math.sin(ang);
  const kv = k[0] * v[0] + k[1] * v[1] + k[2] * v[2];
  const kxv = cross3(/** @type {P3} */ (k), v);
  return [
    v[0] * ca + kxv[0] * sa + k[0] * kv * (1 - ca),
    v[1] * ca + kxv[1] * sa + k[1] * kv * (1 - ca),
    v[2] * ca + kxv[2] * sa + k[2] * kv * (1 - ca),
  ];
}

/**
 * @param {string|number} v
 * @returns {number} uint32
 */
function hashStr(v) {
  if (typeof v === 'number') return v >>> 0;
  let h = 2166136261 >>> 0;
  const s = String(v);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return mix32(h, 0);
}

/**
 * @param {number} h
 * @param {number} salt
 * @returns {number} uint32
 */
function mix32(h, salt) {
  let x = (h ^ salt) >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 2246822507);
  x ^= x >>> 13;
  x = Math.imul(x, 3266489909);
  x ^= x >>> 16;
  return x >>> 0;
}

/**
 * @param {number} x @param {number} y @param {number} z @param {number} seed
 * @returns {number} uint32
 */
function hash3i(x, y, z, seed) {
  const h =
    (Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(z | 0, 0x9e3779b1) ^ seed) >>> 0;
  return mix32(h, 0x85ebca6b);
}

/**
 * @param {number} u32
 * @returns {number} 0..1
 */
const hashToUnit = (u32) => (u32 >>> 0) / 4294967296;
